'use client';

import { RadioGroup } from '@headlessui/react';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import { CampaignType } from 'constants/postType.constants';
import getSymbolFromCurrency from 'currency-symbol-map';
import { cx } from 'cva';
import useCheckBreakpointActive, {
  BreakpointMatch,
} from '@/hooks/useBreakpoint';
import React, { useEffect } from 'react';
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md';
import { useSpringCarousel } from 'react-spring-carousel';
import { isEmpty } from 'lodash-es';
import useUserProfile from '@/hooks/useUserProfile';
import ContributeButton from './ContributionButton';

const customLevel = Object.freeze({
  id: 'custom',
  title: '',
  label: 'Custom',
  quantity: 0,
  description: '',
  autoGenerated: true,
  selected: false,
});

const getGivingLevels = (post) => {
  const currency = post.sourcePostData.currency || 'USD';
  const symbol = getSymbolFromCurrency(currency);
  const levels = [10, 50, 100, 200].map((amount, index) => {
    return {
      id: index,
      title: '',
      label: `${symbol}${amount}`,
      quantity: amount,
      description: '',
      autoGenerated: true,
      selected: index === 0,
    };
  });

  return levels || [];
};

const renderContributeButton = (fundraisingOrg, selectedLevel, post, user) => {
  let recordDonationClickPayload = {
    type: 'post',
    postId: post.id,
    donationClick: {
      // date: { type: Date, default: Date.now },
      userId: user?.id,
      activeCommunityId: user?.activeCommunity?.id,
      location: user?.location,
    },
  };

  //TODO:  Change to Contribution Button
  return (
    <ContributeButton
      fundraisingOrg={fundraisingOrg}
      amount={selectedLevel?.quantity}
      recordPayload={recordDonationClickPayload}
      post={post}>
      Contribute
    </ContributeButton>
  );
};

function CrowdFundingGivingLevels(props) {
  const {
    post,
    className,
    size = 'auto',
    paddingClass = '',
    circularScroll = true,
    noContributeButton = false,
    updateCurrentLevel = () => {},
  } = props;
  const { fundraisingOrg } = post?.renderProps;

  const givingLevels = getGivingLevels(post);

  const isSmallScreen =
    useCheckBreakpointActive('sm', BreakpointMatch.down) || size === 'small';
  const isLargeScreen =
    useCheckBreakpointActive('lg', BreakpointMatch.up) && !isSmallScreen;
  const itemsPerSlide = isLargeScreen ? 4 : 2;
  const gutter = 16;

  const user = useUserProfile();

  const carouselItems = [...givingLevels];
  if (carouselItems.length < itemsPerSlide) {
    const emptyItems = Array(itemsPerSlide - carouselItems.length)
      .fill()
      .map((i, index) => ({ id: `empty-${index}`, empty: true }));
    carouselItems.push(...emptyItems);
  }

  const withLoop = circularScroll && givingLevels.length > itemsPerSlide;
  const showNavigation = withLoop || givingLevels.length > itemsPerSlide;

  const [selectedLevel, setSelectedLevel] = React.useState(
    givingLevels.length ? givingLevels[0] : null,
  );
  const [slideStatus, setSlideStatus] = React.useState({
    next: showNavigation,
    prev: showNavigation,
  });

  const handleLevelSelection = (levelId) => {
    const selected = givingLevels.find((level) => level.id === levelId);
    setSelectedLevel(selected || null);
    updateCurrentLevel(selected || null);
  };

  const renderRadioOption = (level) => {
    if (level.empty) {
      return <div className="h-full w-full" key={level.id} />;
    }

    // TODO: refactor this logic - make it simple
    let isDefaultChecked = false;
    // (!selectedLevel || selectedLevel?.id === 0) &&
    // level.id === 0 &&
    // !noContributeButton;

    // if (noContributeButton) {
    //   isDefaultChecked = false;
    // } else if (selectedLevel?.label === 'custom') {
    //   isDefaultChecked = false;
    // } else if (!selectedLevel && level.id === 0) {
    //   isDefaultChecked = true;
    // } else if (selectedLevel?.id === level.id) {
    //   isDefaultChecked = true;
    // }

    if (selectedLevel?.id === level.id) {
      isDefaultChecked = true;
    }

    return (
      <RadioGroup.Option value={level.id} key={level.id} className="w-full">
        {({ checked }) => (
          <Button
            variant={checked || isDefaultChecked ? 'filled' : 'outlined'}
            color="transparent"
            className={cx(
              checked || isDefaultChecked
                ? 'bg-chips-primary text-text-primary'
                : 'bg-transparent text-primary',
              'w-full border border-chips-primary font-dmsans text-14 font-medium hover:bg-chips-primary hover:text-text-primary',
            )}>
            {level.label}
          </Button>
        )}
      </RadioGroup.Option>
    );
  };

  const {
    carouselFragment,
    slideToPrevItem,
    slideToNextItem,
    getCurrentActiveItem,
  } = useSpringCarousel({
    itemsPerSlide: itemsPerSlide,
    gutter: gutter,
    items: carouselItems.map((level) => ({
      id: level.id,
      renderItem: renderRadioOption(level),
    })),
    withLoop: withLoop,
  });

  const updateScrollStatus = () => {
    if (withLoop) {
      setSlideStatus({ next: true, prev: true });
    } else {
      const activeItem = getCurrentActiveItem();
      if (activeItem) {
        const activeItemIndex = carouselItems.findIndex(
          (item) => item.id === activeItem.id,
        );
        const nextPageItem = carouselItems[activeItemIndex + itemsPerSlide];
        const hasNext = nextPageItem && !nextPageItem.empty;
        const prevPageItem = carouselItems[activeItemIndex - 1];
        const hasPrev = prevPageItem && !prevPageItem.empty;
        setSlideStatus({ next: hasNext, prev: hasPrev });
      }
    }
  };

  useEffect(() => {
    updateScrollStatus();
  }, [givingLevels.length, carouselItems.length]);

  const handleNextClick = () => {
    slideToNextItem();
    updateScrollStatus();
  };

  const handlePrevClick = () => {
    slideToPrevItem();
    updateScrollStatus();
  };

  return (
    <div className={cx('flex flex-col gap-4', className)}>
      <RadioGroup
        className={cx(
          'flex w-full items-center justify-start gap-1',
          paddingClass,
        )}
        onChange={handleLevelSelection}>
        <div style={{ marginRight: showNavigation ? 0 : `${gutter}px` }}>
          {renderRadioOption(customLevel)}
        </div>
        {showNavigation && (
          <Button
            variant="icon"
            onClick={handlePrevClick}
            disabled={!slideStatus.prev}
            className={cx('!p-0', !slideStatus.prev && 'lg:opacity-0')}>
            <MdOutlineChevronLeft className="h-8 w-8 text-text-disabled" />
          </Button>
        )}
        <div className="flex-1 overflow-hidden">{carouselFragment}</div>
        {showNavigation && (
          <Button
            variant="icon"
            disabled={!slideStatus.next}
            onClick={handleNextClick}
            className={cx('!p-0', !slideStatus.next && 'lg:opacity-0')}>
            <MdOutlineChevronRight className="h-8 w-8 text-text-disabled" />
          </Button>
        )}
        {!isSmallScreen && !noContributeButton && (
          <>
            {renderContributeButton(fundraisingOrg, selectedLevel, post, user)}
          </>
        )}
      </RadioGroup>
      {selectedLevel && !selectedLevel.autoGenerated && (
        <div className="w-full bg-body-background">
          <div className="flex flex-col gap-4 px-6 py-4 lg:px-4">
            <Typography variant="subtitle2">{selectedLevel.title}</Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedLevel.description}
            </Typography>
          </div>
        </div>
      )}
      {isSmallScreen && !noContributeButton && (
        <div className="ml-auto px-6">
          {renderContributeButton(fundraisingOrg, selectedLevel, post, user)}
        </div>
      )}
    </div>
  );
}

export default CrowdFundingGivingLevels;
